import React, { createRef, useState, useCallback, useLayoutEffect } from 'react';
import classNames from 'classnames';
import './toolTip.module.scss';
import { useSpring, animated } from 'react-spring';
import { observer } from 'mobx-react';
import { viewportState } from '../../../../commonv2/states/globalStates';
import ResizeObserver from 'resize-observer-polyfill';

function getSize(el?: HTMLElement | null) {
  if (!el) {
    return {
      width: 0,
      height: 0,
    };
  }

  return {
    width: el.offsetWidth,
    height: el.offsetHeight,
  };
}

function useComponentSize(ref?: React.RefObject<HTMLDivElement>) {
  const _useState = useState(getSize(ref ? ref.current : null));
  const ComponentSize = _useState[0];
  const setComponentSize = _useState[1];

  const handleResize = useCallback(
    function handleResize() {
      if (ref?.current) {
        setComponentSize(getSize(ref.current));
      }
    },
    [ref]
  );

  useLayoutEffect(
    function () {
      if (!ref?.current) {
        return;
      }

      handleResize();

      if (typeof ResizeObserver === 'function') {
        let resizeObserver: ResizeObserver | null = new ResizeObserver(function () {
          handleResize();
        });
        resizeObserver.observe(ref.current);

        return function () {
          resizeObserver?.disconnect();
          resizeObserver = null;
        };
      } else {
        window.addEventListener('resize', handleResize);

        return function () {
          window.removeEventListener('resize', handleResize);
        };
      }
    },
    [ref?.current]
  );

  return ComponentSize;
}

const ToolTipContainer = ({
  children,
  id = undefined,
  position = 'top',
  target,
  toolTipRef,
}: {
  children: React.ReactNode;
  id?: string;
  position: string;
  target: React.RefObject<HTMLDivElement>;
  toolTipRef: React.RefObject<HTMLDivElement>;
}) => {
  const targetSize = useComponentSize(target);
  const { width, height } = targetSize;
  const toolTipSize = useComponentSize(toolTipRef);
  const toolTipWidth = toolTipSize.width;
  const toolTipHeight = toolTipSize.height;
  const fromMarginTop = position == 'top' ? -toolTipHeight : height;
  const toMarginTop = position == 'top' ? -(toolTipHeight + 20) : height + 20;
  const marginLeft = viewportState.isMobile ? -(toolTipWidth - 32 - width / 2) : -(toolTipWidth / 2 - width / 2);
  const styles = useSpring({
    from: { opacity: 0, marginTop: fromMarginTop },
    to: { opacity: 1, marginTop: toMarginTop },
  });

  return (
    <animated.div
      id={id}
      style={{ ...styles, marginLeft: marginLeft }}
      styleName={classNames('tooltip', position, { mobile: viewportState.isMobile })}
      ref={toolTipRef}
    >
      {children}
    </animated.div>
  );
};

export type ToolTipPosition = 'top' | 'bottom';

type Props = {
  id?: string;
  title: string;
  description: string;
  position?: ToolTipPosition;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose: () => any;
  opened: boolean;
};

export const ToolTip = observer(
  class ToolTip extends React.Component<Props> {
    containerRef = createRef<HTMLDivElement>();
    toolTipRef = createRef<HTMLDivElement>();

    render() {
      const { id, title, description, position, onClose, opened, children } = this.props;
      const tooltipPosition = position || viewportState.isMobile ? 'bottom' : 'top';
      return (
        <div ref={this.containerRef}>
          {opened && (
            <ToolTipContainer
              id={id}
              position={tooltipPosition}
              target={this.containerRef}
              toolTipRef={this.toolTipRef}
            >
              <div styleName="tooltip-arrow" />
              <div styleName="tooltip-inner">
                <div styleName="tooltip-title">{title}</div>
                <div styleName="tooltip-content">{description}</div>
              </div>
              <div className="tooltip-button" styleName="tooltip-button" onClick={onClose}>
                OK
              </div>
            </ToolTipContainer>
          )}
          {children}
        </div>
      );
    }
  }
);
